import {
  GrowthBook,
  GrowthBookProvider as GBProvider,
} from "@growthbook/growthbook-react";
import { PropsWithChildren, memo, useEffect, useMemo } from "react";

import { trackExperimentViewed } from "../cdp";

const clientKey = import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY;
const apiHost =
  import.meta.env.VITE_GROWTHBOOK_API_HOST || "https://cdn.growthbook.io";
// Dev/test override: force "booking-workflow-variant" to a specific value
// instead of letting GrowthBook resolve it from targeting/experiment rules.
// Unset in staging/training/production — only meant for local .env.local.
const forcedWorkflowVariant = import.meta.env.VITE_GROWTHBOOK_FORCE_VARIANT;

type GrowthBookProviderProps = PropsWithChildren<{
  /** Stable user identifier for consistent experiment assignment across sessions */
  visitorId?: string;
}>;

export const GrowthBookProvider = memo(
  ({ children, visitorId }: GrowthBookProviderProps) => {
    const gb = useMemo(
      () =>
        new GrowthBook({
          apiHost,
          clientKey,
          enableDevMode: import.meta.env.VITE_ENVIRONMENT !== "production",
          trackingCallback: (experiment, result) => {
            window.gtag?.("event", "experiment_viewed", {
              experiment_id: experiment.key,
              variation_id: result.key,
            });
            // Mirror the exposure into the CDP (production only) so experiment
            // results can be computed in the warehouse alongside conversions.
            trackExperimentViewed(experiment.key, result.key);
          },
        }),
      [],
    );

    useEffect(() => {
      if (forcedWorkflowVariant) {
        gb.setForcedFeatures(
          new Map([["booking-workflow-variant", forcedWorkflowVariant]]),
        );
      }

      if (clientKey) {
        gb.init({ streaming: true });
      }

      return () => {
        gb.destroy();
      };
    }, [gb]);

    // Keep attributes in sync whenever visitorId becomes available.
    // GrowthBook uses attributes for experiment targeting and assignment.
    // Merge rather than replace: visitorId resolves asynchronously (deferred
    // FingerprintJS load) and can land after use-growthbook-attributes.ts has
    // already set business attributes (rooms, categoryId, etc.) — a plain
    // setAttributes() would wipe those out and silently flip the resolved
    // variant back to default.
    useEffect(() => {
      if (visitorId) {
        gb.updateAttributes({ id: visitorId });
      }
    }, [gb, visitorId]);

    if (!clientKey) {
      // GrowthBook not configured — render children without A/B testing
      return <>{children}</>;
    }

    return <GBProvider growthbook={gb}>{children}</GBProvider>;
  },
);
